import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { extApi } from '../api/extensibilityClient';
import type { PolicyArea } from '../types';

const CLASSES = [
  { id: 'policy_lever', label: 'Policy lever' },
  { id: 'driver', label: 'Driver' },
  { id: 'intermediate_outcome', label: 'Intermediate outcome' },
  { id: 'constraint', label: 'Constraint' },
  { id: 'tracker', label: 'Tracker / diagnostic' },
  { id: 'final_output', label: 'Final output' },
  { id: 'core_output', label: 'Core output' },
];

export function VariableWizardPage() {
  const [areas, setAreas] = useState<PolicyArea[]>([]);
  const [form, setForm] = useState({
    name: '', id: '', description: '', policy_area_id: 'exports', sub_area: '', unit: '', frequency: 'annual',
    variable_class: 'driver', direction_mode: 'higher_better', source_org: '', source_url: '',
    baseline: '', incremental: '', moderate: '', accelerated: '', transformational: '',
    coefficient: '1', gdp_link: true,
  });
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api.policyAreas().then(setAreas).catch(console.error); }, []);

  const submit = async () => {
    setError(null);
    try {
      const payload = {
        id: form.id || undefined,
        name: form.name,
        description: form.description,
        policy_area_id: form.policy_area_id,
        sub_area: form.sub_area,
        unit: form.unit,
        frequency: form.frequency,
        variable_class: form.variable_class,
        direction_mode: form.direction_mode,
        source_org: form.source_org,
        source_url: form.source_url,
        gdp_link_active: form.gdp_link,
        scenario_inputs: {
          baseline: form.baseline ? parseFloat(form.baseline) : undefined,
          incremental: form.incremental ? parseFloat(form.incremental) : undefined,
          moderate: form.moderate ? parseFloat(form.moderate) : undefined,
          accelerated: form.accelerated ? parseFloat(form.accelerated) : undefined,
          transformational: form.transformational ? parseFloat(form.transformational) : undefined,
        },
        linkage: form.gdp_link ? {
          relationship_type: 'linear',
          formula_expression: 'output = shock * coefficient * weight * phase_in * sign',
          coefficient: parseFloat(form.coefficient || '1'),
          in_gdp_transmission: true,
        } : undefined,
      };
      const v = await extApi.createVariable(payload) as { id: string };
      setResult(`Created variable ${v.id}`);
    } catch (e) {
      setError(String(e));
    }
  };

  const field = (key: keyof typeof form, label: string, type: 'text' | 'number' = 'text') => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
      {label}
      <input className="field" type={type} value={String(form[key])} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </label>
  );

  return (
    <div>
      <h1 className="page-title">Add Variable / Indicator</h1>
      <p className="page-subtitle">Database-driven variable registration — available immediately across Scenario Builder, Explorer, Engine, and Vetting.</p>

      <div className="card grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div><h3>Identity</h3>
          {field('name', 'Variable name *')}
          {field('id', 'Variable ID (optional auto-generated)')}
          {field('description', 'Description')}
          <label style={{ fontSize: '0.85rem' }}>Policy area
            <select className="field" value={form.policy_area_id} onChange={(e) => setForm({ ...form, policy_area_id: e.target.value })}>
              {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          {field('sub_area', 'Sub-area')}
          {field('unit', 'Unit')}
          {field('frequency', 'Frequency')}
        </div>
        <div><h3>Classification & Scenarios</h3>
          <label style={{ fontSize: '0.85rem' }}>Variable type
            <select className="field" value={form.variable_class} onChange={(e) => setForm({ ...form, variable_class: e.target.value })}>
              {CLASSES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.85rem' }}>Direction
            <select className="field" value={form.direction_mode} onChange={(e) => setForm({ ...form, direction_mode: e.target.value })}>
              <option value="higher_better">Higher is better</option>
              <option value="lower_better">Lower is better</option>
              <option value="target_range">Target range</option>
              <option value="neutral">Neutral / monitoring only</option>
            </select>
          </label>
          {field('baseline', 'Baseline', 'number')}
          {field('incremental', 'Incremental', 'number')}
          {field('moderate', 'Moderate', 'number')}
          {field('accelerated', 'Accelerated', 'number')}
          {field('transformational', 'Transformational', 'number')}
        </div>
      </div>

      <div className="card">
        <h3>Data Source & Model Logic</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
          {field('source_org', 'Official agency')}
          {field('source_url', 'Source URL')}
          {field('coefficient', 'GDP linkage coefficient', 'number')}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', fontSize: '0.85rem' }}>
          <input type="checkbox" checked={form.gdp_link} onChange={(e) => setForm({ ...form, gdp_link: e.target.checked })} />
          Create GDP transmission linkage (linear shock × coefficient)
        </label>
        <button className="btn" type="button" style={{ marginTop: '1rem' }} onClick={submit}>Create variable</button>
        {result && <p style={{ color: '#16a34a', marginTop: '0.75rem' }}>{result}</p>}
        {error && <p className="error" style={{ textAlign: 'left', padding: '0.5rem 0' }}>{error}</p>}
      </div>
    </div>
  );
}
